import { baseApi } from "./base.api";
import type { User } from "@/shared/types";
import { ROLES } from "@/shared/constants";
import { Endpoints } from "@/shared/API/APIConfig";
import {
  mapLoginUser,
  type LoginApiUser,
} from "@/shared/lib/authUser";

// Request/Response types
interface LoginRequest {
  email: string;
  password: string;
}

interface LoginResponse {
  user: User;
  token: string;
}

interface RegisterRequest {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface LoginApiPayload {
  token: string;
  user: LoginApiUser;
}

interface LoginApiResponse {
  status?: number;
  success?: boolean;
  message?: string;
  data?: LoginApiPayload;
  token?: string;
  user?: LoginApiUser;
}

const createMockAdminUser = (): User => ({
  id: "1",
  email: "admin@costcontrol.com",
  name: "Admin User",
  roleId: 2,
  roleName: "Admin",
  role: ROLES.ADMIN,
  permissions: [],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

// Auth API endpoints
export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: Endpoints.auth.login,
        method: "POST",
        body: credentials,
      }),
      transformResponse: (response: LoginApiResponse): LoginResponse => {
        const payload = response.data ?? {
          token: response.token as string,
          user: response.user as LoginApiUser,
        };
        return {
          token: payload.token,
          user: mapLoginUser(payload.user),
        };
      },
      invalidatesTags: ["Auth"],
    }),

    register: builder.mutation<LoginResponse, RegisterRequest>({
      queryFn: async (data) => {
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const newUser: User = {
          id: Math.random().toString(36).substring(7),
          email: data.email,
          name: data.name,
          roleId: 4,
          roleName: "branch-staff",
          role: ROLES.BRANCH_STAFF,
          permissions: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        return {
          data: {
            user: newUser,
            token: "mock-jwt-token-12345",
          },
        };
      },
      invalidatesTags: ["Auth"],
    }),

    logout: builder.mutation<void, void>({
      queryFn: async () => {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return { data: undefined };
      },
      invalidatesTags: ["Auth"],
    }),

    getCurrentUser: builder.query<User, void>({
      queryFn: async () => {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return { data: createMockAdminUser() };
      },
      providesTags: ["Auth"],
    }),

    updateProfile: builder.mutation<User, Partial<User>>({
      queryFn: async (data) => {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return {
          data: {
            ...createMockAdminUser(),
            ...data,
          },
        };
      },
      invalidatesTags: ["Auth"],
    }),

    changePassword: builder.mutation<
      void,
      { currentPassword: string; newPassword: string }
    >({
      queryFn: async () => {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return { data: undefined };
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useLogoutMutation,
  useGetCurrentUserQuery,
  useUpdateProfileMutation,
  useChangePasswordMutation,
} = authApi;
